import React, { Dispatch, SetStateAction } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';
import { utcToZonedTime } from 'date-fns-tz';

import { AdaptiveTableCell, IconCell, SliderCheckbox, Tooltip } from '@components/common';
import Actions from '@components/common/Actions';

import { useAppDispatch } from '@store/store';

import { viewOnMap } from '@slices/map';

import { formatDate } from '@utils/kpi-utils/date';
import { getCheckIcon } from '@utils/tableUtils';

import { TAsset } from '@interfaces/assets/asset';
import { TSelectOption } from '@interfaces/components/componentTypes';

import { EAssetRentStatuses } from '@enums/asset';
import {
	EAssetSiteA11yStatus,
	EBackhaulA11yStatus,
	EColorAssetsBy as AssetStatus,
	EPowerSupplyA11yStatus,
	TAssetSiteA11yStatus,
	TBackhaulA11yStatus,
	TPowerSupplyA11yStatus,
} from '@enums/map';

import {
	distanceTooltipMessages,
	formatTemplateDefault,
	formatTemplateWithTime,
	timezoneUTC,
} from '@constants/common';
import { routes } from '@constants/routes';
import { columnSizes } from '@constants/table';
import {
	accessAvailabilityOptions,
	getColorsOptions,
	profilePath,
	removeTooltipText,
	removeTooltipTextRented,
	viewTooltipText,
} from './constants';
import { viewOnMapTooltip } from '@components/routes/AdminPanel/AssetProfile/constants';

import { IAssetVisibilityData } from './AssetsList';

import styles from '@components/common/Table/Table.module.scss';

const { map } = routes;

export interface IGetFiltersParams {
	selectedType: TSelectOption | null;
	selectedSubType: TSelectOption | null;
	assetTypes: TSelectOption[] | [];
	assetSubTypesByType: TSelectOption[] | [];
	assetTypeId: string | null;
	assetSubTypeId: string | null;
	setSelectedType: Dispatch<SetStateAction<TSelectOption>>;
	setSelectedSubType: Dispatch<SetStateAction<TSelectOption>>;
	selectedContactPersonId: TSelectOption | null;
	contactPersonsOptions: TSelectOption[] | [];
	setSelectedContactPersonId: Dispatch<SetStateAction<TSelectOption>>;
	contactPersonId: string | null;
	selectedAccessAvailability: TSelectOption | null;
	setSelectedAccessAvailability: Dispatch<SetStateAction<TSelectOption>>;
	isAvailable: boolean | null;
	selectedBackhaulAccessibilityStatus: TSelectOption | null;
	setSelectedBackhaulAccessibilityStatus: Dispatch<SetStateAction<TSelectOption>>;
	backhaulAccessibilityStatus: TBackhaulA11yStatus | null;
	selectedAssetSiteAvailabilityStatus: TSelectOption | null;
	setSelectedAssetSiteAvailabilityStatus: Dispatch<SetStateAction<TSelectOption>>;
	assetSiteAvailabilityStatus: TAssetSiteA11yStatus | null;
	selectedPowerSupplyAvailabilityStatus: TSelectOption | null;
	setSelectedPowerSupplyAvailabilityStatus: Dispatch<SetStateAction<TSelectOption>>;
	powerSupplyAvailabilityStatus: TPowerSupplyA11yStatus | null;
	selectedIsRentedBy: TSelectOption | null;
	setSelectedIsRentedBy: Dispatch<SetStateAction<TSelectOption>>;
}

export const getColumns = ({
	openDeleteConfirm,
	setSingleIdForRemove,
	handleChangeAssetStatus,
	navigate,
	isEditAccess,
	isEditShowOnMapAccess,
	isMapAvailable,
}: {
	openDeleteConfirm: () => void;
	setSingleIdForRemove: React.Dispatch<React.SetStateAction<string | null>>;
	handleChangeAssetStatus: (dto: IAssetVisibilityData) => void;
	navigate: NavigateFunction;
	isEditAccess: boolean;
	isEditShowOnMapAccess: boolean;
	isMapAvailable: boolean;
}) => ([
	{
		header: 'ID',
		accessorKey: 'assetId',
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			const assetId = getValue();

			return (
				<AdaptiveTableCell
					onDoubleClick={openProfileHandler}
					text={assetId}
				/>
			);
		},
	},
	{
		header: 'Type',
		accessorKey: 'assetTypeName',
		size: columnSizes.size180,
		cell: ({ row: { original } }: CellContext<any, any>) => {
			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			return (
				<IconCell
					id={original.assetType?.icon?.id}
					onClick={openProfileHandler}
					text={original?.assetType.assetTypeName}
				/>
			);
		},
	},
	{
		header: 'SubType',
		accessorKey: 'assetSubTypeName',
		size: columnSizes.size180,
		cell: ({ row: { original } }: CellContext<any, any>) => {
			return (
				<AdaptiveTableCell text={original?.assetSubType?.assetSubTypeName} />
			);
		},
	},
	{
		header: 'Address',
		accessorKey: 'address',
		enableSorting: false,
		cell: ({
			row: {
				original:
					{
						adPropertyNoName,
						adStreetName,
						adTownCity,
						adLocality,
						adPostCode,
						id,
					},
			},
		}: { row: { original: Record<string, string> } }) => {
			const openProfileHandler = () => navigate(`${profilePath}/${id}`);

			return (
				<AdaptiveTableCell
					onDoubleClick={openProfileHandler}
					text={`
							${adPropertyNoName ? `${adPropertyNoName}, ` : ''}
							${adStreetName ? `${adStreetName}, ` : ''}
							${adLocality ? `${adLocality}, ` : ''}
							${adTownCity ? `${adTownCity}, ` : ''}
							${adPostCode || ''}
						`}
				/>
			);
		},
	},
	{
		header: 'contact person',
		accessorKey: 'contactPerson',
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const contactPerson = getValue();
			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			return (
				<AdaptiveTableCell
					onDoubleClick={openProfileHandler}
					text={
						contactPerson
							? `${contactPerson?.firstName} ${contactPerson?.lastName}`
							: ''
					}
				/>
			);
		},
	},
	{
		header: 'Price',
		accessorKey: 'indicativeAcceptablePricing',
		size: columnSizes.size130,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const indicativeAcceptablePricing = getValue();
			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			return (
				<AdaptiveTableCell
					onDoubleClick={openProfileHandler}
					text={`£${indicativeAcceptablePricing}`}
				/>
			);
		},
	},
	{
		header: () => <>Showed<br />on map</>,
		accessorKey: 'showOnMap',
		size: columnSizes.size115,
		isHidden: !isEditShowOnMapAccess,
		cell: ({ row: { original } }: CellContext<any, any>) => {
			const changeStatus = () => {
				handleChangeAssetStatus({
					dto: { assetIds: [original?.id] },
					isHide: original?.showOnMap,
				});
			};

			const isRented = original?.rentStatus?.name === EAssetRentStatuses.active;

			const isSliderDisabled = isRented || original?.isIncomingRequests;

			return (
				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-start',
						alignItems: 'center',
						paddingLeft: '15px',
					}}
				>
					<SliderCheckbox
						checked={original?.showOnMap}
						disabled={isSliderDisabled}
						id={original?.id}
						onChange={changeStatus}
						tooltipText={isSliderDisabled
							? 'The asset can’t be disabled as it’s been rented by MNOs'
							: undefined
						}
					/>
				</div>
			);
		},
	},
	{
		header: () => <>Access<br />Availability</>,
		accessorKey: 'isAvailable',
		size: columnSizes.size150,
		enableSorting: false,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const isAvailable = getValue();
			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			return (
				<AdaptiveTableCell
					onDoubleClick={openProfileHandler}
					text={isAvailable ? 'Yes' : 'No'}
				/>
			);
		},
	},
	{
		header: () => <>Backhaul<br />Accessibility</>,
		accessorKey: AssetStatus.backhaulAccessibility,
		enableSorting: false,
		size: columnSizes.size150,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const value = getValue();
			const tooltipMessages = distanceTooltipMessages as any;
			const tooltipText = tooltipMessages[value?.name];

			const uncheckIcon = getCheckIcon(EBackhaulA11yStatus, value?.name);

			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			return (
				<div
					onDoubleClick={openProfileHandler}
					style={{
						display: 'flex',
						justifyContent: 'flex-start',
						alignItems: 'center',
						paddingLeft: '15px',
					}}
				>
					<Tooltip
						content={tooltipText}
						offset={[15, 0]}
						placement='top-end'
					>
						<div>
							{uncheckIcon}
						</div>
					</Tooltip>
				</div>
			);
		},
	},
	{
		header: () => <>Site<br />Availability</>,
		accessorKey: AssetStatus.siteAvailability,
		enableSorting: false,
		size: columnSizes.size150,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const value = getValue();
			const tooltipMessages = distanceTooltipMessages as any;
			const tooltipText = tooltipMessages[value?.name];

			const uncheckIcon = getCheckIcon(EAssetSiteA11yStatus, value?.name);

			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			return (
				<div
					onDoubleClick={openProfileHandler}
					style={{
						display: 'flex',
						justifyContent: 'flex-start',
						alignItems: 'center',
						paddingLeft: '15px',
					}}
				>
					<Tooltip
						content={tooltipText}
						offset={[15, 0]}
						placement='top-end'
					>
						<div>
							{uncheckIcon}
						</div>
					</Tooltip>
				</div>
			);
		},
	},
	{
		header: () => <>Power Supply<br />Availability</>,
		accessorKey: AssetStatus.powerSupplyAvailability,
		enableSorting: false,
		size: columnSizes.size150,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const value = getValue();
			const tooltipMessages = distanceTooltipMessages as any;
			const tooltipText = tooltipMessages[value?.name];

			const uncheckIcon = getCheckIcon(EPowerSupplyA11yStatus, value?.name);

			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			return (
				<div
					onDoubleClick={openProfileHandler}
					style={{
						display: 'flex',
						justifyContent: 'flex-start',
						alignItems: 'center',
						paddingLeft: '15px',
					}}
				>
					<Tooltip
						content={tooltipText}
						offset={[15, 0]}
						placement='top-end'
					>
						<div>
							{uncheckIcon}
						</div>
					</Tooltip>
				</div>
			);
		},
	},
	{
		header: 'Rented',
		accessorKey: 'rentStatus',
		enableSorting: false,
		size: columnSizes.size150,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();

			return (
				<AdaptiveTableCell text={value?.name === EAssetRentStatuses.active ? 'yes' : 'no'} />
			);
		},
	},
	{
		header: 'Created',
		accessorKey: 'createdAt',
		size: columnSizes.size120,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const createdAt = getValue();
			const date = formatDate(utcToZonedTime(createdAt, timezoneUTC), formatTemplateDefault);

			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			return (
				<AdaptiveTableCell
					onDoubleClick={openProfileHandler}
					text={date}
				/>
			);
		},
	},
	{
		header: 'Updated',
		accessorKey: 'updatedAt',
		size: columnSizes.size190,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const updatedAt = getValue();
			const date = formatDate(utcToZonedTime(updatedAt, timezoneUTC), formatTemplateWithTime);

			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			return (
				<AdaptiveTableCell
					onDoubleClick={openProfileHandler}
					text={date}
				/>
			);
		},
	},
	{
		header: () => (
			<>
				Last verification
				<br />
				date
			</>
		),
		accessorKey: 'lastVerificationDate',
		size: columnSizes.size190,
		cell: ({ getValue, row: { original } }: CellContext<any, any>) => {
			const lastVerificationDate = getValue();
			const date = lastVerificationDate ? formatDate(+lastVerificationDate) : '';

			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			return (
				<AdaptiveTableCell
					onDoubleClick={openProfileHandler}
					text={date}
				/>
			);
		},
	},
	{
		header: () => (
			<div className={styles.centerColumnHeader}>
				Actions
			</div>
		),
		accessorKey: 'actions',
		enableSorting: false,
		size: columnSizes.size135,
		cell: function Cell({ row: { original } }: CellContext<any, any>) {
			const dispatch = useAppDispatch();

			const showOnMap = original?.showOnMap;
			const isRented = original?.rentStatus?.name === EAssetRentStatuses.active;
			const isDisabledDeleteAsset = isRented || original?.isIncomingRequests;

			const handleDelete = () => {
				setSingleIdForRemove(original?.id);
				openDeleteConfirm();
			};

			const handleViewOnMap = () => {
				if (!showOnMap) return;

				const marker: TAsset = original;

				void dispatch(viewOnMap({ marker }));

				navigate(map.index, { state: { isRedirectedFromAdminPanel: true } });
			};

			const openProfileHandler = () => navigate(`${profilePath}/${original?.id}`);

			return (
				<Actions
					deleteTooltip={isDisabledDeleteAsset ? removeTooltipTextRented : removeTooltipText}
					handleDelete={handleDelete}
					isDelete={isEditAccess}
					isDeleteDisable={isDisabledDeleteAsset}
					isDisabledViewOnMap={!showOnMap}
					isView
					isViewOnMap={isMapAvailable}
					viewHandler={openProfileHandler}
					viewOnMapHandler={handleViewOnMap}
					viewOnMapTooltip={viewOnMapTooltip}
					viewTooltip={viewTooltipText}
				/>
			);
		},
	},
]);

export function getFilters({
	selectedType,
	selectedSubType,
	assetTypes,
	assetSubTypesByType,
	setSelectedType,
	selectedContactPersonId,
	contactPersonsOptions,
	setSelectedContactPersonId,
	contactPersonId,
	selectedAccessAvailability,
	setSelectedAccessAvailability,
	isAvailable,
	selectedBackhaulAccessibilityStatus,
	setSelectedBackhaulAccessibilityStatus,
	backhaulAccessibilityStatus,
	selectedAssetSiteAvailabilityStatus,
	setSelectedAssetSiteAvailabilityStatus,
	assetSiteAvailabilityStatus,
	selectedPowerSupplyAvailabilityStatus,
	setSelectedPowerSupplyAvailabilityStatus,
	powerSupplyAvailabilityStatus,
	selectedIsRentedBy,
	setSelectedIsRentedBy,
	setSelectedSubType,
	assetTypeId,
	assetSubTypeId,
}: IGetFiltersParams) {
	return [
		{
			name: 'assetType',
			placeholder: 'Type',
			option: selectedType,
			options: assetTypes,
			minWidth: '200px',
			setState: setSelectedType,
			defaultValue: assetTypeId,
		},
		{
			name: 'assetSubType',
			placeholder: 'Subtype',
			option: selectedSubType,
			options: assetSubTypesByType,
			minWidth: '200px',
			setState: setSelectedSubType,
			defaultValue: assetSubTypeId,
			disabled: !selectedType || !assetSubTypesByType.length,
		},
		{
			name: 'ContactPerson',
			placeholder: 'Contact person',
			option: selectedContactPersonId,
			options: contactPersonsOptions,
			minWidth: '200px',
			setState: setSelectedContactPersonId,
			defaultValue: contactPersonId,
		},
		{
			name: 'accessAvailability',
			placeholder: 'Access Availability',
			option: selectedAccessAvailability,
			options: accessAvailabilityOptions,
			minWidth: '200px',
			setState: setSelectedAccessAvailability,
			defaultValue: isAvailable,
		},
		{
			name: 'backhaulAccessibilityStatus',
			placeholder: 'Backhaul Accessibility',
			option: selectedBackhaulAccessibilityStatus,
			options: getColorsOptions(EBackhaulA11yStatus),
			minWidth: '200px',
			setState: setSelectedBackhaulAccessibilityStatus,
			defaultValue: backhaulAccessibilityStatus,
		},
		{
			name: 'assetSiteAvailabilityStatus',
			placeholder: 'Site Availability',
			option: selectedAssetSiteAvailabilityStatus,
			options: getColorsOptions(EAssetSiteA11yStatus),
			minWidth: '200px',
			setState: setSelectedAssetSiteAvailabilityStatus,
			defaultValue: assetSiteAvailabilityStatus,
		},
		{
			name: 'powerSupplyAvailabilityStatus',
			placeholder: 'Power Supply Availability',
			option: selectedPowerSupplyAvailabilityStatus,
			options: getColorsOptions(EPowerSupplyA11yStatus),
			minWidth: '200px',
			setState: setSelectedPowerSupplyAvailabilityStatus,
			defaultValue: powerSupplyAvailabilityStatus,
		},
		{
			name: 'rentedBy',
			placeholder: 'Rented',
			option: selectedIsRentedBy,
			options: [
				{ label: 'All', value: 'All' },
				{ label: 'Yes', value: 'Yes' },
				{ label: 'No', value: 'No' },
			],
			setState: setSelectedIsRentedBy,
			defaultValue: null,
		},
	];
}

export const getConfirmModalQuestionText = (isHide: boolean) => {
	return `Are you sure you want to ${isHide ? 'hide' : 'show'} selected assets on the map?`;
};
